import React from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Booking } from "@/types/calendar";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import bookingService from '@/services/bookingService';
import { useToast } from "@/hooks/use-toast";
import { useCalendarData } from "@/hooks/useCalendarData";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type CalendarStatus = 'booked' | 'tentative' | 'event';

interface BaseCalendarProps {
  currentMonth: Date;
  onMonthChange: (direction: "prev" | "next") => void;
  bookings?: Booking[];
  showTooltips?: boolean;
  className?: string;
  showNavigation?: boolean;
  onSelectDate?: (date: Date) => void;
  selectedDate?: Date;
  isCompact?: boolean;
  userType?: "talent" | "promoter";
  currentUserId?: string;
  showBookingDialog?: boolean;
}

const BaseCalendar: React.FC<BaseCalendarProps> = ({
  currentMonth,
  onMonthChange,
  bookings = [],
  showTooltips = true,
  className = "",
  showNavigation = true,
  onSelectDate,
  selectedDate,
  isCompact = false,
  userType = "talent",
  currentUserId,
  showBookingDialog = false,
}) => {
  const { bookings: userBookings, calendarAction } = useCalendarData(currentUserId);
  const { toast } = useToast();
  // Add this new state for the mobile dialog
  const [selectedBookings, setSelectedBookings] = React.useState<{
    date: string;
    bookings: Booking[];
  } | null>(null);

  // Add new state for confirmation
  const [bookingToAdd, setBookingToAdd] = React.useState<Booking | null>(null);

  // Function to get bookings for a specific date
  const getDateBookings = (dateStr: string) => {
    return bookings.filter(booking => booking.eventDate === dateStr);
  };

  // Function to get the dominant status for a date
  const getDateStatus = (dateStr: string): CalendarStatus | null => {
    const dateBookings = getDateBookings(dateStr);
    if (dateBookings.length === 0) return null;
    
    if (userType === 'promoter') {
      return 'event';
    }
    
    return dateBookings.some(booking => booking.status === 'confirmed') 
      ? 'booked' 
      : null;
  };

  // Function to generate days for the current month
  const daysInMonth = React.useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: (Date | null)[] = [];

    const firstDayOfWeek = firstDay.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  }, [currentMonth]);

  const statusColors = React.useMemo(() => {
    if (userType === "talent") {
      return {
        booked: isCompact ? "bg-blue-300" : "bg-blue-500/20",
        tentative: isCompact ? "bg-yellow-300" : "bg-yellow-500/20",
      };
    } else {
      return {
        event: isCompact ? "bg-green-500" : "bg-green-500/20",
        booked: isCompact ? "bg-green-500" : "bg-green-500/20",
      };
    }
  }, [isCompact, userType]);

  // Function to format time for display
  const formatTime = (time: string) => {
    return new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleDateClick = (date: Date) => {
    if (onSelectDate) {
      onSelectDate(date);
    }

    // Only show dialog if showBookingDialog is true
    if (showBookingDialog) {
      const dateStr = format(date, "yyyy-MM-dd");
      const dateBookings = getDateBookings(dateStr);
      
      if (dateBookings.length > 0) {
        setSelectedBookings({
          date: dateStr,
          bookings: dateBookings,
        });
      }
    }
  };

  // Add this function to format the date for the dialog header
  const formatDateForHeader = (dateStr: string) => {
    return format(new Date(dateStr), "MMMM d, yyyy");
  };

  // Modify the isEventInCalendar function to use userBookings
  const isEventInCalendar = (booking: Booking) => {
    if (!userBookings) return false;
    
    return userBookings.some(existingBooking => 
      existingBooking.eventDate === booking.eventDate &&
      existingBooking.eventName === booking.eventName &&
      existingBooking.talentCallTime === booking.talentCallTime
    );
  };

  const handleAddToCalendar = async (booking: Booking) => {
    if (!currentUserId) return;

    try {
      const formData = new FormData();
      formData.append('action', 'add');
      formData.append('eventDate', booking.eventDate);
      formData.append('talentCallTime', booking.talentCallTime);
      formData.append('showStartTime', booking.showStartTime);
      formData.append('eventName', booking.eventName);
      formData.append('location', booking.location);
      // Only append venueName if it exists
      if (booking.venueName) {
        formData.append('venueName', booking.venueName);
      }
      if (booking.notes) formData.append('notes', booking.notes);

      await calendarAction(formData);
      
      toast({
        title: "Event Added",
        description: "The event has been added to your calendar",
        variant: "default",
      });
      
      setSelectedBookings(null);
      setBookingToAdd(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add event to your calendar",
        variant: "destructive",
      });
    }
  };

  React.useEffect(() => {
    console.log('Current bookings:', bookings);
  }, [bookings]);

  return (
    <div className={className}>
      {showNavigation && (
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              onClick={() => onMonthChange("prev")}
            >
              {"<"}
            </Button>
            <div className="text-sm text-gray-400 w-24 text-center">
              {format(currentMonth, "MMM yyyy")}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              onClick={() => onMonthChange("next")}
            >
              {">"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-7 gap-1 mb-2">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <div
            key={index}
            className="text-center text-xs font-medium text-gray-400 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {daysInMonth.map((date, index) => {
          if (!date) {
            return (
              <div
                key={`empty-${index}`}
                className="aspect-square flex items-center justify-center text-xs text-gray-700"
              />
            );
          }

          const dateStr = format(date, "yyyy-MM-dd");
          const dateBookings = getDateBookings(dateStr);
          const status = getDateStatus(dateStr);
          const isToday = date.toDateString() === new Date().toDateString();
          const isSelected = selectedDate?.toDateString() === date.toDateString();

          const dayContent = (
            <div
              onClick={() => handleDateClick(date)}
              className={`
                aspect-square flex items-center justify-center text-xs
                text-gray-300 cursor-pointer hover:bg-gray-800 relative
                ${isToday ? "border border-gray-400" : ""}
                ${isSelected ? "bg-gray-800" : ""}
                ${
                  !isCompact && status
                    ? userType === "promoter"
                      ? statusColors["event"]
                      : statusColors[status as keyof typeof statusColors]
                    : ""
                }
              `}
            >
              <span className={`z-10 ${isToday ? "font-medium" : ""}`}>
                {date.getDate()}
              </span>
              {status && (
                <div
                  className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full
                    ${
                      isCompact
                        ? userType === "promoter" || (userType === "talent" && !isCompact)
                          ? statusColors["event"]
                          : statusColors[status as keyof typeof statusColors]
                        : status === "booked"
                        ? "bg-blue-300"
                        : status === "tentative"
                        ? "bg-yellow-300"
                        : "bg-green-500"
                    }`}
                />
              )}
            </div>
          );

          if (showTooltips && dateBookings.length > 0) {
            return (
              <TooltipProvider key={dateStr}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {dayContent}
                  </TooltipTrigger>
                  <TooltipContent className="p-2 max-w-xs">
                    <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar">
                      {dateBookings.length > 3 && (
                        <div className="text-xs text-gray-400 font-medium mb-2">
                          {dateBookings.length} events
                        </div>
                      )}
                      {dateBookings.map((booking, idx) => (
                        <div key={idx} className="text-xs">
                          {idx > 0 && <div className="border-t border-gray-700 my-2" />}
                          <div className="font-medium">
                            {booking.eventName || "Unnamed Event"}
                          </div>
                          <div className="text-gray-400 text-[11px]">
                            {booking.venueName || booking.location}
                          </div>
                          <div className="text-gray-400">
                            {formatTime(booking.talentCallTime)} - {formatTime(booking.showStartTime)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }

          return React.cloneElement(dayContent, { key: dateStr });
        })}
      </div>

      {/* Update the Dialog styling */}
      <Dialog open={selectedBookings !== null} onOpenChange={() => setSelectedBookings(null)}>
        <DialogContent className="bg-gray-900 border-gray-800 [&>button]:text-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-white">
              {selectedBookings && formatDateForHeader(selectedBookings.date)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {selectedBookings?.bookings.map((booking, idx) => (
              <div key={idx} className="p-3 bg-gray-800 rounded-lg">
                <div className="font-semibold text-white text-base">
                  {booking.eventName || "Unnamed Event"}
                </div>
                <div className="text-sm text-gray-300 mt-1">
                  {booking.venueName || booking.location}
                </div>
                <div className="text-sm text-gray-400 mt-2">
                  {formatTime(booking.talentCallTime)} - {formatTime(booking.showStartTime)}
                </div>
                {booking.notes && (
                  <div className="text-sm text-gray-400 mt-1">
                    {booking.notes}
                  </div>
                )}
                {booking.talentId !== currentUserId && (
                  !isEventInCalendar(booking) ? (
                    <Button
                      onClick={() => setBookingToAdd(booking)}
                      className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                    >
                      Add to My Calendar
                    </Button>
                  ) : (
                    <div className="mt-3 text-sm text-gray-400 text-center">
                      Already in your calendar
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog 
        open={bookingToAdd !== null} 
        onOpenChange={(open) => !open && setBookingToAdd(null)}
      >
        <AlertDialogContent className="bg-gray-900 border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Add Event to Calendar
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {bookingToAdd && (
                <>
                  Do you want to add "{bookingToAdd.eventName}" on{' '}
                  {format(new Date(bookingToAdd.eventDate), "MMMM d, yyyy")} to your calendar?
                  <div className="mt-2">
                    Time: {formatTime(bookingToAdd.talentCallTime)} - {formatTime(bookingToAdd.showStartTime)}
                  </div>
                  {bookingToAdd.location && (
                    <div className="mt-1">
                      Location: {bookingToAdd.location}
                    </div>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-gray-800 text-white hover:bg-gray-700"
              onClick={() => setBookingToAdd(null)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => bookingToAdd && handleAddToCalendar(bookingToAdd)}
            >
              Add Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BaseCalendar;